import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import {
  alpha,
  Avatar,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  MenuItem,
  styled,
} from "@mui/material";
import CompanyAPI from "apis/CompanyAPI";
import FlexBox from "components/FlexBox";
import { H6, Small, Tiny } from "components/Typography";
import { SettingsContext } from "contexts/SettingsContext";
import useAuth from "hooks/useAuth";
import {
  FC,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { THEMES } from "utils/constants";
import PopoverLayout from "./PopoverLayout";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  borderRadius: 10,
  margin: "0 8px",
  minHeight: 48,
  paddingInline: 12,
  "& .MuiTypography-root, & .MuiBox-root": {
    color: theme.palette.text.primary,
  },
  "&:hover": {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.2 : 0.08,
    ),
  },
  "& .MuiListItemIcon-root": {
    minWidth: 34,
    color: theme.palette.text.secondary,
  },
}));

const SectionLabel = styled(Tiny)(({ theme }) => ({
  display: "block",
  paddingInline: 16,
  marginBottom: 6,
  fontWeight: 700,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
}));

const CompanyAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.14),
  color: theme.palette.primary.main,
  fontWeight: 700,
}));

const TriggerAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.14),
  color: theme.palette.primary.main,
  fontWeight: 700,
}));

const ProfilePopover: FC = () => {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const { settings, saveSettings } = useContext(SettingsContext);
  const [open, setOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  const isDarkMode = settings.theme === THEMES.DARK;
  const companyName = typeof user?.name === "string" ? user.name : "Empresa";
  const userEmail = typeof user?.email === "string" ? user.email : "";

  const companyInitials = useMemo(() => {
    const words = companyName.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "EM";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }, [companyName]);

  const handleMenuItem = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleThemeToggle = () => {
    saveSettings({
      ...settings,
      theme: isDarkMode ? THEMES.LIGHT : THEMES.DARK,
    });
  };

  useEffect(() => {
    let imageUrl: string | null = null;
    let mounted = true;

    CompanyAPI.getLogo()
      .then((blob) => {
        if (!mounted || blob.size <= 0) return;
        imageUrl = URL.createObjectURL(blob);
        setCompanyLogo(imageUrl);
      })
      .catch(() => {
        setCompanyLogo(null);
      });

    return () => {
      mounted = false;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, []);

  return (
    <Fragment>
      <ButtonBase disableRipple ref={anchorRef} onClick={() => setOpen(true)}>
        <FlexBox alignItems="center" gap={0.5} pr={0.5}>
          <TriggerAvatar src={companyLogo || undefined} alt={companyName}>
            {companyInitials}
          </TriggerAvatar>
          <KeyboardArrowDownRoundedIcon
            sx={{ fontSize: 19, color: "text.secondary" }}
          />
        </FlexBox>
      </ButtonBase>

      <PopoverLayout
        hiddenViewButton
        maxWidth={260}
        minWidth={220}
        popoverOpen={open}
        anchorRef={anchorRef}
        popoverClose={() => setOpen(false)}
        title={
          <FlexBox alignItems="center">
            <CompanyAvatar src={companyLogo || undefined} alt={companyName}>
              {companyInitials}
            </CompanyAvatar>

            <Box ml={1}>
              <H6>{companyName}</H6>
              <Tiny display="block" fontWeight={500} color="text.disabled">
                {userEmail}
              </Tiny>
            </Box>
          </FlexBox>
        }
      >
        <Box pt={1}>
          <SectionLabel>{t("Conta")}</SectionLabel>

          <StyledMenuItem
            onClick={() => handleMenuItem("/main/administration")}
          >
            <ListItemIcon>
              <AdminPanelSettingsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Small>{t("Administração")}</Small>
          </StyledMenuItem>

          <StyledMenuItem onClick={handleThemeToggle}>
            <ListItemIcon>
              {isDarkMode ? (
                <LightModeRoundedIcon fontSize="small" />
              ) : (
                <DarkModeRoundedIcon fontSize="small" />
              )}
            </ListItemIcon>
            <Small>
              {isDarkMode ? "Ativar tema claro" : "Ativar tema escuro"}
            </Small>
          </StyledMenuItem>

          <Divider sx={{ my: 1 }} />

          <StyledMenuItem
            onClick={() => {
              logout();
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <LogoutRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Small>{t("Sair")}</Small>
          </StyledMenuItem>
        </Box>
      </PopoverLayout>
    </Fragment>
  );
};

export default ProfilePopover;
